import { observable, action, computed, runInAction, makeObservable } from 'mobx'
import api from '../../api'
import { dataStore } from '../UIStore'
import { DEFAULT_COLOUR, fieldError } from '../../components/tools/toolbar/ManageModalControls.jsx'

// State for the "Manage tags" modal (ManageTagsContent). Owned by ManageModalStore.
class TagsModalStore {
  @observable items = []
  @observable loading = true
  @observable saving = false
  @observable error = ''
  @observable newName = ''
  @observable newColour = DEFAULT_COLOUR
  @observable filter = ''
  @observable editingId = null
  @observable picker = null
  @observable colourChange = null
  @observable deleteItem = null

  constructor () {
    makeObservable(this)
  }

  @computed get filteredItems () {
    const normalizedFilter = this.filter.trim().toLowerCase()
    return normalizedFilter
      ? this.items.filter(item => item.name.toLowerCase().includes(normalizedFilter))
      : this.items
  }

  @action setNewName (name) { this.newName = name }
  @action setFilter (filter) { this.filter = filter }
  @action setPicker (picker) { this.picker = picker }
  @action setColourChange (colourChange) { this.colourChange = colourChange }

  // Keeps dataStore.userTags (used outside this modal) in step with every successful change.
  @action setItems (items) {
    this.items = items
    dataStore.setUserTags(items)
  }

  @action togglePicker (picker) {
    this.picker = this.picker === picker ? null : picker
  }

  @action requestDelete (item) {
    this.picker = null
    this.deleteItem = item
  }

  @action cancelDelete () {
    this.deleteItem = null
  }

  @action closeOverlays () {
    this.deleteItem = null
    this.colourChange = null
    this.picker = null
  }

  // Clears the add/edit form back to an empty "new tag" state.
  @action cancelEdit () {
    this.editingId = null
    this.newName = ''
    this.newColour = DEFAULT_COLOUR
  }

  @action open () {
    this.cancelEdit()
    this.filter = ''
    this.closeOverlays()
    this.error = ''
    this.loadTags()
  }

  async loadTags () {
    runInAction(() => {
      this.loading = true
      this.error = ''
    })
    try {
      const response = await api.getUserTags()
      this.setItems(Array.isArray(response.data) ? response.data : [])
    } catch (err) {
      runInAction(() => {
        this.error = fieldError(err) || 'Unable to load tags.'
        this.items = []
      })
    } finally {
      runInAction(() => { this.loading = false })
    }
  }

  // Creates a tag, or saves the one being edited. Resolves true on success.
  async submit () {
    const name = this.newName.trim()
    if (!name || this.saving) return false
    const editingId = this.editingId
    runInAction(() => {
      this.saving = true
      this.error = ''
    })
    try {
      if (editingId) {
        const response = await api.updateUserTag(editingId, { name, color: this.newColour })
        this.setItems(this.items.map(item => item.id === editingId ? response.data : item))
      } else {
        const response = await api.createUserTag({ name, color: this.newColour })
        this.setItems(this.items.concat(response.data))
      }
      runInAction(() => {
        this.cancelEdit()
        this.picker = null
      })
      return true
    } catch (err) {
      const fallback = editingId ? 'Unable to update tag.' : 'Unable to add tag.'
      runInAction(() => { this.error = fieldError(err, 'name') || fieldError(err, 'color') || fallback })
      return false
    } finally {
      runInAction(() => { this.saving = false })
    }
  }

  @action selectColour (colour) {
    if (this.picker === 'add') {
      this.newColour = colour
      this.picker = null
      return
    }
    const item = this.items.find(entry => entry.id === this.picker)
    this.picker = null
    if (!item || item.color.toLowerCase() === colour.toLowerCase()) return
    this.colourChange = { item, nextColor: colour }
  }

  async confirmColourChange () {
    const colourChange = this.colourChange
    if (!colourChange || this.saving) return
    runInAction(() => {
      this.saving = true
      this.error = ''
    })
    try {
      const response = await api.updateUserTag(colourChange.item.id, {
        color: colourChange.nextColor
      })
      runInAction(() => {
        this.setItems(this.items.map(item => (
          item.id === colourChange.item.id ? response.data : item
        )))
        this.colourChange = null
      })
    } catch (err) {
      runInAction(() => { this.error = fieldError(err, 'color') || 'Unable to update tag colour.' })
    } finally {
      runInAction(() => { this.saving = false })
    }
  }

  @action startEdit (item) {
    this.editingId = item.id
    this.newName = item.name
    this.newColour = item.color
    this.picker = null
    this.error = ''
  }

  async confirmDelete () {
    const deleteItem = this.deleteItem
    if (!deleteItem || this.saving) return
    runInAction(() => {
      this.saving = true
      this.error = ''
    })
    try {
      await api.deleteUserTag(deleteItem.id)
      runInAction(() => {
        this.setItems(this.items.filter(item => item.id !== deleteItem.id))
        if (this.editingId === deleteItem.id) this.cancelEdit()
        this.deleteItem = null
      })
    } catch (err) {
      runInAction(() => { this.error = fieldError(err) || 'Unable to delete tag.' })
    } finally {
      runInAction(() => { this.saving = false })
    }
  }
}

export default TagsModalStore
